/**
 * The opposition.
 *
 * Read by silhouette first. Programmer art is an advantage here: a tall thin
 * thing, a squat wide thing, and a small floating thing are told apart at a
 * glance and across a dark room, which is more than can be said for five
 * humanoids in different jackets. Each shape does one job, and the shape tells
 * you which job before it has done anything.
 */

export type EnemyBehaviour = 'chase' | 'swarm' | 'ranged' | 'brute' | 'floater' | 'bomber' | 'shielder';

/** Rough body plan, which is what the eye actually reads at distance. */
export type EnemyBuild = 'biped' | 'crawler' | 'stalker' | 'hulk' | 'drone' | 'orb' | 'turret';

/**
 * What an enemy does to you, rather than what it is made of.
 *
 * An enemy is a tactical verb. Health and damage are how much of it there is;
 * this is the question it asks, and it is what the encounter builder composes
 * with — two enemies that ask the same question are one enemy twice, and two
 * that ask opposite ones are a fight.
 */
export type Pressure =
  | 'close'     // comes at you, so standing still is not an option
  | 'suppress'  // owns a sightline, so moving in the open is not either
  | 'deny'      // makes a place dangerous rather than a direction
  | 'evade'     // will not hold still to be shot
  | 'block'     // cannot be shot from the front
  | 'support';  // makes the others harder, and dies easily itself

export interface EnemyType {
  id: string;
  name: string;
  behaviour: EnemyBehaviour;
  build: EnemyBuild;
  health: number;
  speed: number;
  /** Half-width of the body box. */
  radius: number;
  height: number;
  colour: number;
  headColour: number;
  /** Melee reach, or projectile range for shooters. */
  range: number;
  interval: number;
  damage: number;
  score: number;
  /** Floaters hover this far off the ground. */
  hover?: number;
  /** Ranged types try to hold this distance. */
  standoff?: number;
  /** Depth at which this type starts appearing. */
  from: number;
  weight: number;
  /** Explodes on death, hurting whatever is close. */
  detonates?: number;
  /** Takes reduced damage from the front. */
  frontArmour?: number;
  /** Speeds up as it gets hurt. */
  enrages?: boolean;
  /** What it asks of you. */
  pressure: Pressure;
  /**
   * Seconds of visible wind-up before the shot lands.
   *
   * A big hit with no telegraph is not difficulty, it is a dice roll. Anything
   * that can take a third of your health announces itself first and gives you
   * the time to be somewhere else.
   */
  telegraph?: number;
  /** Health per second returned to every ally within `range`. */
  heals?: number;
  /** Id of a type it builds, and how many of them it may have standing. */
  deploys?: string;
  deployCap?: number;
  /** Lobs its shot over cover instead of firing it flat. */
  lobs?: boolean;
  /** Disappears by itself after this many seconds. */
  decays?: number;
}

export const ENEMY_TYPES: EnemyType[] = [
  {
    id: 'grunt', build: 'biped', name: 'Grunt', behaviour: 'chase',
    health: 78, speed: 2.6, radius: 0.42, height: 1.75,
    colour: 0xb3372f, headColour: 0xe6a06a, range: 2.2, interval: 0.9, damage: 9, score: 10,
    from: 1, weight: 10,
    pressure: 'close',
  },
  {
    id: 'runner', build: 'crawler', name: 'Runner', behaviour: 'swarm',
    // Small, fast, and low to the ground: the silhouette says "coming at you".
    health: 42, speed: 4.4, radius: 0.3, height: 1.15,
    colour: 0xe2643a, headColour: 0xffc08a, range: 1.9, interval: 0.65, damage: 6, score: 12,
    from: 2, weight: 8,
    pressure: 'close',
  },
  {
    id: 'spitter', build: 'stalker', name: 'Spitter', behaviour: 'ranged',
    // Tall and narrow, hangs back and shoots. You want to close on it.
    health: 88, speed: 2.2, radius: 0.34, height: 2.15,
    colour: 0x7a4fbf, headColour: 0xc9a7ff, range: 26, interval: 1.9, damage: 11, score: 18,
    standoff: 13, from: 2, weight: 6,
    pressure: 'suppress',
  },
  {
    id: 'brute', build: 'hulk', name: 'Brute', behaviour: 'brute',
    // Squat and wide. Slow enough to walk away from, painful if you do not.
    health: 390, speed: 1.9, radius: 0.72, height: 2.1,
    colour: 0x6d3a2a, headColour: 0xa8654a, range: 2.7, interval: 1.4, damage: 24, score: 40,
    from: 4, weight: 3,
    pressure: 'close',
  },
  {
    id: 'drone', build: 'drone', name: 'Drone', behaviour: 'floater',
    // Hovers above head height and shoots down, so it breaks the habit of
    // only ever looking along the floor.
    health: 68, speed: 3.0, radius: 0.38, height: 0.7,
    colour: 0x2f8fb3, headColour: 0x9fe4ff, range: 22, interval: 1.6, damage: 9, score: 22,
    hover: 2.6, standoff: 10, from: 4, weight: 5,
    pressure: 'deny',
  },
  {
    id: 'warden', build: 'stalker', name: 'Warden', behaviour: 'ranged',
    // The late-floor problem: tanky, accurate, and it does not come to you.
    health: 234, speed: 2.4, radius: 0.46, height: 2.3,
    colour: 0x3f6b45, headColour: 0xbfe3a8, range: 34, interval: 1.6, damage: 14, score: 45,
    standoff: 17, from: 8, weight: 3,
    pressure: 'suppress',
  },
  {
    id: 'popper', build: 'orb', name: 'Popper', behaviour: 'bomber',
    // A ball that sprints at you and bursts. Reads instantly as "do not let
    // this touch you", and it is the one enemy you should walk away from.
    health: 58, speed: 4.9, radius: 0.36, height: 0.78,
    colour: 0xd8a021, headColour: 0xfff0a0, range: 2.0, interval: 0.5, damage: 26, score: 26,
    hover: 0.45, from: 3, weight: 5, detonates: 34,
    pressure: 'deny',
  },
  {
    id: 'bulwark', build: 'hulk', name: 'Bulwark', behaviour: 'shielder',
    // Armoured from the front, so it has to be flanked. The first enemy that
    // asks you to move rather than shoot harder.
    health: 312, speed: 2.0, radius: 0.62, height: 2.0,
    colour: 0x46566b, headColour: 0x9fc0e0, range: 2.6, interval: 1.3, damage: 20, score: 42,
    from: 5, weight: 3, frontArmour: 0.22,
    pressure: 'block',
  },
  {
    id: 'wretch', build: 'crawler', name: 'Wretch', behaviour: 'swarm',
    // Cheap, fast, and it gets faster as you hurt it. Late floors need
    // something that punishes leaving a job half done.
    health: 104, speed: 3.6, radius: 0.32, height: 1.05,
    colour: 0x8e3f6b, headColour: 0xf0a0d0, range: 1.9, interval: 0.6, damage: 8, score: 20,
    from: 5, weight: 6, enrages: true,
    pressure: 'close',
  },
  {
    id: 'lancer', build: 'biped', name: 'Lancer', behaviour: 'ranged',
    // Mid-range, mobile, and it does not hold still like the spitter does.
    health: 143, speed: 3.1, radius: 0.38, height: 1.9,
    colour: 0x1f7a6a, headColour: 0x8ff0d8, range: 20, interval: 1.15, damage: 10, score: 30,
    standoff: 9, from: 3, weight: 5,
    pressure: 'evade',
  },

  // ---------------------------------------------------------------------------
  // The ones that change the shape of the fight rather than adding to it.

  {
    id: 'leech', build: 'orb', name: 'Leech', behaviour: 'ranged',
    // Heals everything around it and does nothing to you at all, which makes
    // it the only enemy whose answer is "shoot the other one first".
    health: 166, speed: 3.2, radius: 0.34, height: 0.9,
    colour: 0x2f9f6a, headColour: 0xb6ffd8, range: 10, interval: 1.0, damage: 0, score: 38,
    hover: 1.7, standoff: 8, from: 7, weight: 4,
    pressure: 'support', heals: 11,
  },
  {
    id: 'sentinel', build: 'stalker', name: 'Sentinel', behaviour: 'ranged',
    // Owns a lane. Winds up for the better part of a second with a line drawn
    // down it, which is the whole fight: be behind something by then.
    health: 203, speed: 1.7, radius: 0.4, height: 2.45,
    colour: 0x8a2f4a, headColour: 0xffb0c8, range: 60, interval: 2.8, damage: 27, score: 58,
    standoff: 26, from: 9, weight: 3,
    pressure: 'suppress', telegraph: 0.95,
  },
  {
    id: 'mortar', build: 'drone', name: 'Mortar', behaviour: 'floater',
    // Arcs a shell over whatever you are hiding behind and paints where it
    // will land. Cover stops being an answer; moving is.
    health: 120, speed: 2.1, radius: 0.46, height: 0.82,
    colour: 0xc06a2a, headColour: 0xffcf8a, range: 36, interval: 3.1, damage: 21, score: 44,
    hover: 3.6, standoff: 19, from: 10, weight: 3,
    pressure: 'deny', lobs: true, telegraph: 1.15,
  },
  {
    id: 'engineer', build: 'biped', name: 'Engineer', behaviour: 'ranged',
    // Builds turrets and hides behind them. The arena changes while you are
    // busy with something else, and interrupting that is worth more than the
    // damage it is doing.
    health: 177, speed: 2.9, radius: 0.4, height: 1.85,
    colour: 0xb0a03a, headColour: 0xfff4a0, range: 20, interval: 6.0, damage: 0, score: 52,
    standoff: 15, from: 6, weight: 3,
    pressure: 'support', deploys: 'turret', deployCap: 2,
  },
  {
    id: 'turret', build: 'turret', name: 'Turret', behaviour: 'ranged',
    // Never rolled: only ever built. Rusts away by itself, so a floor cannot
    // be extended for ever by something that keeps making more of them.
    health: 88, speed: 0, radius: 0.44, height: 1.05,
    colour: 0x6f6a5e, headColour: 0xffb060, range: 24, interval: 0.85, damage: 7, score: 12,
    standoff: 6, from: 99, weight: 0,
    pressure: 'suppress', decays: 26,
  },
];

/** Built during a fight rather than rolled into a roster. */
export function typeById(id: string): EnemyType | undefined {
  return ENEMY_TYPES.find((t) => t.id === id);
}

export function typesFor(depth: number): EnemyType[] {
  return ENEMY_TYPES.filter((t) => depth >= t.from && t.weight > 0);
}

/**
 * Fights worth having.
 *
 * Rolling each enemy independently by rarity gives you a bag of enemies, and a
 * bag of enemies is not an encounter — it is the same fight every time with
 * different hit points. What makes a fight is a pair that asks two things at
 * once and takes away the obvious answer to each: something that punishes
 * standing still next to something that punishes moving, a shield in front of
 * the thing actually hurting you, a healer behind something that outlasts you.
 *
 * So a floor is built out of pairs. Each one names the vice it puts you in,
 * because if it cannot be said in a sentence it is not a fight, it is a mob.
 */
export interface Pairing {
  /** Both must be available at this depth for the pairing to be used. */
  a: string;
  b: string;
  /** What the player has to solve. */
  bind: string;
  from: number;
  weight: number;
}

/*
 * When each of these first appears, and why it is spread out.
 *
 * The whole schedule sits a floor earlier than it did, from two down to six.
 * Evening out the spike at seven left the run with six floors nobody loses —
 * 100, 100, 100, 99, 99, 100 — and a median run of about ten, so more than
 * half of it could not be failed. A first floor still has to be survivable by
 * somebody who has never held a mouse and that one is untouched; the Spitter
 * now arrives on two rather than three, the Lancer on three, the Drone on
 * four and the Bulwark on five, and the fight built around the Bulwark on
 * six. Nothing past seven moves, because every type is already out by then.
 *
 * Three rules, all learned from the clear rate rather than argued for. A floor
 * gets at most one thing that changes what you have to *do* — floor seven used
 * to debut the Leech and the Engineer together, one making your damage stop
 * working and the other building the arena while you are busy, and it cost 62
 * health against floor six's 36 and floor eight's 63. It was a floor eight
 * arriving a floor early.
 *
 * And a pairing never unlocks on the floor its members arrive. Meeting the
 * Bulwark and meeting the two fights built around the Bulwark on the same
 * floor is the same mistake in a different place, and floor six and floor
 * eight both had it.
 *
 * And at most one thing that fights from range. Two standoff debuts on one
 * floor has produced the dip twice now — the Leech and the Engineer on seven,
 * then the Warden and the Engineer on eight, which cleared at 77% between two
 * floors at 86% and carried the highest rescue rate in the table. Things that
 * hang back do not come to you, so the floor drags and the director ends up
 * dragging them there instead.
 *
 * The three rules interact, which is how floor seven ended up at 81% between
 * two floors at 99% and 90%. Holding a pairing back off its members' debut
 * floor pushed both of the Bulwark fights off six and onto seven, where they
 * landed on top of the Leech arriving — so the collision was not removed, it
 * was moved one floor along. A floor that debuts something which changes what
 * you have to do gets one new pairing, not two.
 */
export const PAIRINGS: Pairing[] = [
  { a: 'grunt', b: 'spitter', bind: 'cover from one is exposure to the other', from: 3, weight: 6 },
  { a: 'runner', b: 'spitter', bind: 'you cannot hold still and cannot ignore the rush', from: 3, weight: 6 },
  /*
   * Lighter than its siblings, because it lands harder than any of them.
   *
   * Floor seven's damage was 23% Popper and 21% Bulwark — 44% from one bind —
   * where floor six is 25% Popper with no Bulwark and floor eight is 20%
   * Bulwark with no Popper, and both of those clear at 94% against floor
   * seven's 78%. Either half is a floor; together they are a wall. Floor seven
   * has only four binds to draw from, so a weight of five put this on a
   * quarter of them.
   */
  { a: 'popper', b: 'bulwark', bind: 'the thing you must back away from is behind the thing you must walk around', from: 6, weight: 3 },
  { a: 'bulwark', b: 'lancer', bind: 'the obvious target blocks damage while the real one keeps moving', from: 8, weight: 5 },
  { a: 'drone', b: 'brute', bind: 'looking up costs you ground', from: 5, weight: 4 },
  { a: 'wretch', b: 'warden', bind: 'finishing the swarm takes the time the warden needs', from: 9, weight: 4 },
  { a: 'engineer', b: 'warden', bind: 'the arena changes while you are pinned', from: 9, weight: 4 },
  { a: 'engineer', b: 'bulwark', bind: 'you cannot reach the builder without going round the shield', from: 9, weight: 4 },
  { a: 'leech', b: 'brute', bind: 'the tank stops dying until you deal with the support', from: 8, weight: 5 },
  { a: 'leech', b: 'wretch', bind: 'an evasive group you have to push into rather than clear from range', from: 8, weight: 4 },
  { a: 'sentinel', b: 'runner', bind: 'the lane is watched and the room will not let you wait', from: 10, weight: 5 },
  { a: 'mortar', b: 'grunt', bind: 'standing behind cover is the mistake', from: 11, weight: 5 },
  { a: 'mortar', b: 'bulwark', bind: 'the shield holds you in the open where the shell is landing', from: 11, weight: 4 },
  { a: 'sentinel', b: 'leech', bind: 'a long shot you cannot out-trade and a healer you cannot reach', from: 10, weight: 3 },
];

/**
 * How the floor scales.
 *
 * Floor one has to be survivable by someone who has never held a mouse, and
 * the honest test of that is standing still: three slow things doing half
 * damage gives them about a minute to work out which way is forward, and a
 * minute is enough. Everything ramps from there, and damage ramps slowest —
 * more enemies reads as pressure, more damage per hit just reads as unfair.
 */
export interface WaveShape {
  budget: number;
  healthScale: number;
  speedScale: number;
  damageScale: number;
  concurrent: number;
  /** Seconds of quiet at the start of the floor. */
  grace: number;
}

/**
 * How far into the opening stretch a floor is, curved.
 *
 * Zero on floor one, one from floor seven on, and concave between — steepest
 * exactly where the curve used to be flattest.
 *
 * Everything below used to be linear in the depth, which put floors two
 * through six at a tenth to a half of floor seven's pressure and produced six
 * floors in a row that cleared at 98% or better. Against a median run of about
 * ten floors, more than half of one could not be failed. Bringing the enemy
 * types forward a floor each did nothing about it, and the measurement said
 * why: the roster is bought from a fixed budget with costs tuned so a point of
 * Spitter is worth a point of Bulwark, so a richer table changes what is in
 * the room and not how much. Only the budget does that.
 *
 * The end of the ramp is unmoved. Floor seven is exactly what it was, and so
 * is everything past it — this steepens the approach rather than raising the
 * ceiling. Floor one is untouched by construction, because it still has to be
 * survivable by somebody who has never held a mouse.
 */
function opening(depth: number): number {
  const t = Math.min(1, Math.max(0, (depth - 1) / 6));
  return t ** 0.62;
}

/*
 * How much of the floor arrives at once, on the way up.
 *
 * Separate from `opening` and deliberately more generous, because the two
 * levers do not feel the same. The first pass raised all three of budget,
 * damage and concurrency along one curve: the health each early floor cost
 * roughly doubled — seven, fifteen, thirty, thirty-six against three, eleven,
 * nineteen, twenty-eight — and the clear rate did not move off 100% until
 * floor five. The floors got expensive without becoming losable.
 *
 * More of them at once is the lever that changes that, and it is the one this
 * file already argues for: a floor with more enemies on it reads as pressure,
 * where the same floor hitting harder per shot just reads as unfair. So the
 * count runs ahead of the rest of the ramp.
 */
function arriving(depth: number): number {
  const t = Math.min(1, Math.max(0, (depth - 1) / 6));
  return t ** 0.45;
}

export function waveShape(depth: number): WaveShape {
  const d = depth - 1;
  const up = opening(depth);
  const many = arriving(depth);
  /*
   * The count stops growing; the difficulty does not.
   *
   * A run has no end, so the budget used to grow for ever and floor thirty was
   * sixty-odd things to find in a map you had already crossed twice. Sixty is
   * not harder than thirty, it is longer, and length is the one thing an
   * endless run already has too much of. Past about floor thirteen the surplus
   * goes into how hard each one is instead.
   */
  const wanted = depth <= 7 ? Math.round(55 + 384 * many) : 55 + d * 64;
  const cap = 820;
  const beyond = Math.max(0, wanted - cap) / cap;
  return {
    // Total "points" of enemy the floor is allowed to contain at once.
    budget: Math.round(Math.min(cap, wanted)),
    healthScale: 1 + d * 0.17 + beyond * 1.5,
    speedScale: Math.min(1.4, 0.82 + d * 0.05),
    damageScale: Math.min(1.6, depth <= 7 ? 0.45 + 0.72 * up : 0.45 + d * 0.12),
    concurrent: depth === 1
      ? 2
      : Math.min(24, depth <= 7 ? 3 + Math.round(10 * many) : 3 + Math.round((d - 1) * 1.9)),
    /*
     * Quiet at the start of a floor, and much less of it on the first one.
     *
     * Six seconds was a kindness aimed at somebody working out the controls,
     * and what it actually bought was a first floor that took fourteen seconds
     * to show anybody a hostile — measured. The opening enemy is now standing
     * on the map when the map appears, so the grace is only about the *second*
     * one, and three is plenty of room to look around.
     */
    grace: depth === 1 ? 3 : depth === 2 ? 3 : 1.6,
  };
}

export function cost(t: EnemyType): number {
  return Math.round(t.health * 0.35 + t.damage * 1.6 + (t.standoff ? 12 : 0));
}

// -----------------------------------------------------------------------------

/**
 * Fill a floor's worth of enemies.
 *
 * Not by rolling each one independently: that gives you a bag of enemies, and
 * a bag of enemies is the same fight every time with different hit points. A
 * floor is seeded with a *pairing* — two types whose answers contradict each
 * other, so solving one costs you the other — and the rest of the budget is
 * filled in around it.
 *
 * Lives here rather than in the run because it is a question about the enemy
 * table and nothing else: no scene, no player, no pathing. Which also means it
 * can be tested by calling it.
 */
export function rollRoster(
  depth: number,
  budget: number,
  rand: () => number,
): { roster: EnemyType[]; bind: string } {
  const types = typesFor(depth);
  const roster: EnemyType[] = [];
  let left = budget;
  const cheapest = Math.min(...types.map(cost));

  const available = new Map(types.map((t) => [t.id, t]));
  const pairs = PAIRINGS.filter((p) => depth >= p.from && available.has(p.a) && available.has(p.b));
  let bind = '';
  if (pairs.length && depth >= 3) {
    const total = pairs.reduce((a, p) => a + p.weight, 0);
    let roll = rand() * total;
    let chosen = pairs[0];
    for (const p of pairs) {
      roll -= p.weight;
      if (roll <= 0) {
        chosen = p;
        break;
      }
    }
    const a = available.get(chosen.a)!;
    const b = available.get(chosen.b)!;
    // Enough of each that the vice is real rather than incidental: about a
    // third of the floor, and never so much that nothing else appears.
    const share = budget * 0.36;
    /*
     * And a ceiling on the overshoot, because "about a third" was not.
     *
     * The loop tests `spent < share` before each push, so one expensive member
     * can carry it far past the share on its own — a Brute costs 137 of a 503
     * budget, and `leech + brute` on floor eight spent 400 of it on four
     * bodies. The rest of the floor was whatever 100 points would buy, which
     * is nothing, so the vice was the entire fight rather than a third of it.
     */
    const ceiling = share * 1.3;
    let spent = 0;
    for (let i = 0; i < 12 && spent < share; i++) {
      const t = i % 2 === 0 ? a : b;
      if (cost(t) > left) break;
      // Never take a step that lands past the ceiling — unless nothing has
      // been taken yet, since a pairing of one is not a pairing.
      if (roster.length >= 2 && spent + cost(t) > ceiling) break;
      roster.push(t);
      left -= cost(t);
      spent += cost(t);
    }
    if (roster.length >= 2) bind = chosen.bind;
  }

  /*
   * A floor is a number of bodies as well as an amount of enemy.
   *
   * The fill took anything it could afford, so it would happily spend the last
   * 175 of a 503 budget on a second Brute and hand back a floor of four —
   * `brute, lancer, brute, leech` on floor eight, which is a heavy fight but
   * barely a floor, and it reads as an empty map with two tanks in it. Enough
   * is kept back that what is left can still become bodies.
   */
  const want = Math.max(3, Math.round(budget / 70));
  let guard = 0;
  while (left >= cheapest && guard++ < 200) {
    // What must still be affordable after this pick, to reach a floor's worth.
    const reserve = Math.max(0, want - roster.length - 1) * cheapest;
    const affordable = types.filter((t) => cost(t) <= Math.max(cheapest, left - reserve));
    const total = affordable.reduce((a, t) => a + t.weight, 0);
    let roll = rand() * total;
    let pick = affordable[0];
    for (const t of affordable) {
      roll -= t.weight;
      if (roll <= 0) {
        pick = t;
        break;
      }
    }
    roster.push(pick);
    left -= cost(pick);
  }

  // Shuffled, so the pairing is not simply the first four things through the
  // door — it should be the shape of the floor, not its opening move.
  for (let i = roster.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [roster[i], roster[j]] = [roster[j], roster[i]];
  }
  return { roster, bind };
}
